import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Phone, MessageCircle, Check, MapPin, Clock, Users, Car, FileText, Calculator, Building } from 'lucide-react';
import { Button } from '../components/ui/button';
import { COMPANY_INFO } from '../mock';
import { InternalLinksSection } from '../components/InternalLinksSection';

const UnfallgutachtenWiesbadenPage = () => {
  const [formData, setFormData] = useState({ name: '', phone: '', message: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    await fetch(`${API_URL}/api/leads/submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...formData, source: 'unfallgutachten-wiesbaden-landing-page' })
    });
    alert('Vielen Dank! Wir melden uns schnellstmöglich.');
    setFormData({ name: '', phone: '', message: '' });
  };

  const faqs = [
    {
      q: "Wie schnell bekomme ich einen Termin in Wiesbaden?",
      a: "In Wiesbaden und dem Rheingau-Taunus-Kreis vergeben wir Termine meist innerhalb von 24-48 Stunden. Bei dringenden Fällen – etwa nach einem Unfall auf der A66 oder der Schiersteiner Brücke – können wir oft noch am selben Tag reagieren. Durch unser bundesweites Netzwerk haben wir auch in der hessischen Landeshauptstadt erfahrene Gutachter vor Ort."
    },
    {
      q: "Wer trägt die Kosten für ein Unfallgutachten in Wiesbaden?",
      a: "Bei einem unverschuldeten Unfall übernimmt die gegnerische Haftpflichtversicherung die Kosten für das Gutachten vollständig. Sie haben das gesetzliche Recht auf freie Gutachterwahl – lassen Sie sich nicht von der Versicherung zu deren Gutachter drängen. Gerade bei den vielen Premiumfahrzeugen in Wiesbaden und Umgebung ist ein unabhängiges Gutachten besonders wichtig."
    },
    {
      q: "Welche Gebiete rund um Wiesbaden deckt ihr ab?",
      a: "Wir decken ganz Wiesbaden ab – von der Innenstadt und dem Kurpark bis Biebrich am Rhein, von Dotzheim bis Mainz-Kastel. Auch der gesamte Rheingau-Taunus-Kreis gehört zu unserem Einsatzgebiet: Eltville, Rüdesheim, Idstein, Bad Schwalbach, Taunusstein und alle umliegenden Gemeinden. Wir kommen dorthin, wo Ihr Fahrzeug steht."
    },
    {
      q: "Was ist bei einem Unfall auf der A66 oder der Schiersteiner Brücke zu beachten?",
      a: "Die A66 und die Schiersteiner Brücke (A643) sind Unfallschwerpunkte im Raum Wiesbaden – hohe Verkehrsdichte und Staus führen häufig zu Auffahrunfällen. Nach einem Autobahnunfall: Sichern Sie die Unfallstelle, dokumentieren Sie mit Fotos und rufen Sie uns an. Wir kommen zur Werkstatt oder zum Abstellort und erstellen dort das Gutachten."
    },
    {
      q: "Macht ihr auch Gutachten in Mainz und Frankfurt?",
      a: "Ja, selbstverständlich! Wiesbaden liegt ideal im Rhein-Main-Gebiet. Mainz ist nur über die Theodor-Heuss-Brücke entfernt (10 Min.), Frankfurt über die A66 erreichbar (30 Min.). Für diese Städte haben wir eigene Landingpages mit lokalen Informationen. Unser gesamtes Einsatzgebiet umfasst das Rhein-Main-Gebiet und darüber hinaus."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Unfallgutachten Wiesbaden | Kfz-Sachverständige vor Ort | Dein Gutachten Direkt</title>
        <meta name="description" content="Unfallgutachten in Wiesbaden, Rheingau-Taunus-Kreis und Rhein-Main. Über 10 Jahre Erfahrung, gerichtsfeste Gutachten. Termine in 24-48h. Kostenlos bei unverschuldetem Unfall. ☎ Jetzt anrufen!" />
        <meta name="keywords" content="Unfallgutachten Wiesbaden, Kfz Gutachter Wiesbaden, Sachverständiger Wiesbaden, Unfallgutachter Rheingau, Gutachter Taunus, Kfz Gutachten Wiesbaden, Gutachter Eltville, Gutachter Taunusstein" />
        <link rel="canonical" href="https://www.dein-gutachten-direkt.de/unfallgutachten-wiesbaden" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Dein Gutachten Direkt - Wiesbaden & Rheingau-Taunus",
            "description": "Kfz-Sachverständigenbüro mit über 10 Jahren Erfahrung. Unabhängige Unfallgutachten in Wiesbaden, Rheingau-Taunus-Kreis und dem gesamten Rhein-Main-Gebiet.",
            "telephone": "+4941018471160",
            "email": "info@dein-gutachten-direkt.de",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Wiesbaden",
              "addressRegion": "Hessen",
              "addressCountry": "DE"
            },
            "areaServed": [
              {"@type": "City", "name": "Wiesbaden"},
              {"@type": "City", "name": "Mainz"},
              {"@type": "City", "name": "Eltville am Rhein"},
              {"@type": "City", "name": "Taunusstein"},
              {"@type": "City", "name": "Bad Schwalbach"},
              {"@type": "City", "name": "Idstein"},
              {"@type": "City", "name": "Rüdesheim am Rhein"}
            ],
            "priceRange": "€€"
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.q,
              "acceptedAnswer": { "@type": "Answer", "text": faq.a }
            }))
          })}
        </script>
      </Helmet>

      <Header />

      <main>
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-gray-900 via-black to-gray-800 pt-28 pb-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-green/20 border border-brand-green/30 rounded-full mb-6">
                  <Building className="w-4 h-4 text-brand-green" />
                  <span className="text-brand-green font-semibold text-sm">Wiesbaden & Rheingau-Taunus</span>
                </div>

                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                  Unfallgutachten
                  <br />
                  <span className="text-brand-green">in Wiesbaden</span>
                </h1>
                
                <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                  Unabhängige Kfz-Sachverständige mit <strong className="text-white">über 10 Jahren Erfahrung</strong>. 
                  Gerichtsfeste Gutachten in der hessischen Landeshauptstadt und dem gesamten Rheingau-Taunus-Kreis.
                </p>

                <div className="grid grid-cols-2 gap-4 mb-8">
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                    <span>Über 10 Jahre Erfahrung</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                    <span>Rheingau-Taunus-Kreis</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                    <span>Gerichtsfeste Gutachten</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                    <span>Zusammenarbeit mit Anwälten</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                    <span>Termine in 24-48h</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                    <span>Kostenlose Erstberatung</span>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <a href={`tel:${COMPANY_INFO.phone}`}>
                    <Button size="lg" className="w-full sm:w-auto bg-brand-green hover:bg-brand-green-dark text-white text-lg px-8 py-6 rounded-xl font-bold">
                      <Phone className="w-5 h-5 mr-2" />
                      Jetzt anrufen
                    </Button>
                  </a>
                  <a href={`https://wa.me/${COMPANY_INFO.whatsapp}`} target="_blank" rel="noopener noreferrer">
                    <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-white text-white hover:bg-white/10 text-lg px-8 py-6 rounded-xl font-bold">
                      <MessageCircle className="w-5 h-5 mr-2" />
                      WhatsApp
                    </Button>
                  </a>
                </div>

                <p className="text-gray-400 text-sm mt-6">
                  Bei unverschuldetem Unfall: Kostenübernahme durch gegnerische Versicherung
                </p>
              </div>

              <div className="bg-white/5 backdrop-blur border border-white/10 rounded-3xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Einsatzgebiet Wiesbaden & Rheingau</h2>
                
                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-4">
                    <MapPin className="w-6 h-6 text-brand-green flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-white font-semibold">Abgedeckte Gebiete</p>
                      <p className="text-gray-400">Ganz Wiesbaden + Rheingau-Taunus-Kreis</p>
                      <p className="text-gray-400">Eltville, Taunusstein, Idstein, Bad Schwalbach</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Clock className="w-6 h-6 text-brand-green flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-white font-semibold">Terminvergabe</p>
                      <p className="text-gray-400">Meist innerhalb 24-48 Stunden</p>
                      <p className="text-gray-400">Bundesweites Gutachternetzwerk</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Users className="w-6 h-6 text-brand-green flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-white font-semibold">Unser Team</p>
                      <p className="text-gray-400">Mehrere erfahrene Sachverständige</p>
                      <p className="text-gray-400">Hauptsitz Rellingen, bundesweit tätig</p>
                    </div>
                  </div>
                </div>

                <div className="bg-brand-green/20 border border-brand-green/30 rounded-xl p-4">
                  <p className="text-brand-green font-semibold text-sm">
                    <strong>Freie Gutachterwahl:</strong> Bei unverschuldetem Unfall dürfen Sie Ihren Gutachter selbst wählen. Die Versicherung muss die Kosten übernehmen – egal ob in Wiesbaden oder bundesweit.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Über uns Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-12">
              <span className="text-brand-green font-semibold text-sm uppercase tracking-wider">Über uns</span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2">Ihr Kfz-Gutachter für Wiesbaden & Rheingau</h2>
            </div>

            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed text-lg">
                <strong>Dein Gutachten Direkt</strong> ist Ihr unabhängiger Kfz-Sachverständiger für Wiesbaden und den Rheingau-Taunus-Kreis. 
                Mit unserem Hauptsitz in Rellingen bei Hamburg und einem bundesweiten Netzwerk sind wir auch in der 
                <strong> hessischen Landeshauptstadt</strong> schnell für Sie da.
              </p>

              <p className="text-gray-700 leading-relaxed text-lg">
                Wiesbaden ist geprägt von seiner besonderen Lage: Die Stadt zwischen Taunus und Rhein, 
                mit der vielbefahrenen A66 und der Schiersteiner Brücke, erlebt täglich zahlreiche Verkehrsunfälle. 
                Seit über <strong>10 Jahren</strong> helfen wir Geschädigten, ihre Ansprüche <strong>gerichtsfest</strong> zu dokumentieren.
              </p>

              <p className="text-gray-700 leading-relaxed text-lg">
                Unser Team arbeitet eng mit <strong>Fachanwälten für Verkehrsrecht</strong> in Wiesbaden und der Region zusammen. 
                Bei Bedarf können wir Sie direkt an einen spezialisierten Anwalt weitervermitteln – kostenlos und unverbindlich.
              </p>

              <div className="bg-gray-50 rounded-2xl p-8 my-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Das zeichnet uns aus:</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0 mt-1" />
                    <span><strong>Unabhängig:</strong> Wir arbeiten ausschließlich für Sie – nicht für Versicherungen oder Werkstätten</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0 mt-1" />
                    <span><strong>Erfahren:</strong> Über 10 Jahre Erfahrung in der Kfz-Schadensbewertung</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0 mt-1" />
                    <span><strong>Gerichtsfest:</strong> Unsere Gutachten sind rechtssicher und vor Gericht verwendbar</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0 mt-1" />
                    <span><strong>Anwaltsnetzwerk:</strong> Enge Zusammenarbeit mit Verkehrsrechtsanwälten in Wiesbaden</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0 mt-1" />
                    <span><strong>Bundesweit:</strong> Wir sind in ganz Deutschland für Sie da</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Was beinhaltet unser Gutachten */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-12">
              <span className="text-brand-green font-semibold text-sm uppercase tracking-wider">Leistungen</span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2">Was beinhaltet unser Gutachten?</h2>
              <p className="text-xl text-gray-600 mt-4">Alle Ansprüche dokumentieren – mehr als nur Reparaturkosten</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-brand-green/50 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <Calculator className="w-6 h-6 text-brand-green" />
                  <h3 className="font-bold text-gray-900 text-lg">Reparaturkostenkalkulation</h3>
                </div>
                <p className="text-gray-700">Detaillierte Berechnung nach Herstellervorgaben: Arbeitslohn, Original-Ersatzteile, Lackierung und alle Nebenkosten. In Wiesbaden mit den üblichen Stundenverrechnungssätzen der Region.</p>
              </div>
              
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-brand-green/50 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <Car className="w-6 h-6 text-brand-green" />
                  <h3 className="font-bold text-gray-900 text-lg">Wertminderung</h3>
                </div>
                <p className="text-gray-700">Der merkantile Minderwert nach dem Unfall. Besonders bei den vielen Premiumfahrzeugen in Wiesbaden oft 400-1.200€ oder mehr – auch nach perfekter Reparatur.</p>
              </div>
              
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-brand-green/50 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <Clock className="w-6 h-6 text-brand-green" />
                  <h3 className="font-bold text-gray-900 text-lg">Nutzungsausfall</h3>
                </div>
                <p className="text-gray-700">Entschädigung für jeden Tag ohne Ihr Fahrzeug: 23€ (Kleinwagen) bis 175€ (Oberklasse). Bei 10 Tagen Werkstattzeit schnell 350-600€ zusätzlich.</p>
              </div>
              
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-brand-green/50 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <FileText className="w-6 h-6 text-brand-green" />
                  <h3 className="font-bold text-gray-900 text-lg">Wiederbeschaffungswert</h3>
                </div>
                <p className="text-gray-700">Bei Totalschaden: Was kostet ein gleichwertiges Ersatzfahrzeug auf dem Wiesbadener Markt? Wir recherchieren aktuelle Angebote in der Region.</p>
              </div>
              
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-brand-green/50 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <Calculator className="w-6 h-6 text-brand-green" />
                  <h3 className="font-bold text-gray-900 text-lg">Restwertermittlung</h3>
                </div>
                <p className="text-gray-700">Der Verkaufswert des beschädigten Fahrzeugs bei Totalschaden. Wir holen mehrere Angebote ein für den bestmöglichen Preis.</p>
              </div>
              
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-brand-green/50 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <FileText className="w-6 h-6 text-brand-green" />
                  <h3 className="font-bold text-gray-900 text-lg">Fotodokumentation</h3>
                </div>
                <p className="text-gray-700">30-50 professionelle Fotos dokumentieren jeden Schaden. Diese Beweissicherung ist im Streitfall vor Gericht entscheidend.</p>
              </div>
            </div>

            <div className="bg-brand-green/10 border-2 border-brand-green/30 rounded-2xl p-6 mt-8 text-center">
              <p className="text-gray-900 font-semibold">
                <strong>Rechenbeispiel Wiesbaden:</strong> Ein Audi A4 mit 4.500€ Reparaturkosten, 600€ Wertminderung und 8 Tagen Nutzungsausfall (472€) 
                = 1.072€ mehr als nur der Kostenvoranschlag!
              </p>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Häufige Fragen zu Unfallgutachten in Wiesbaden</h2>
            <div className="space-y-4">
              {faqs.map((faq, idx) => (
                <div key={idx} className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                  <h3 className="font-bold text-gray-900 mb-3 text-lg">{faq.q}</h3>
                  <p className="text-gray-700 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stadtteile */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Wiesbaden & Rheingau-Taunus-Kreis</h2>
            <p className="text-lg text-gray-600 mb-8">Alle Wiesbadener Stadtteile und die gesamte Region</p>
            <div className="flex flex-wrap justify-center gap-3">
              {['Wiesbaden-Innenstadt', 'Biebrich', 'Mainz-Kastel', 'Mainz-Kostheim', 'Dotzheim', 'Bierstadt', 'Sonnenberg', 'Schierstein', 'Eltville', 'Rüdesheim', 'Taunusstein', 'Bad Schwalbach', 'Idstein', 'Oestrich-Winkel', 'Geisenheim', 'Mainz', 'Hofheim', 'Limburg'].map((ort) => (
                <span key={ort} className="px-4 py-2 bg-white rounded-lg border border-gray-200 text-gray-700 font-medium hover:border-brand-green/50 transition-colors">
                  {ort}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-gradient-to-br from-gray-900 to-black">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold text-white mb-6">Jetzt Gutachten in Wiesbaden beauftragen</h2>
            <p className="text-xl text-gray-300 mb-8">Wiesbaden & Rheingau-Taunus • Termine in 24-48 Stunden</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={`tel:${COMPANY_INFO.phone}`}>
                <Button size="lg" className="bg-brand-green hover:bg-brand-green-dark text-white text-lg px-10 py-6 rounded-xl font-bold">
                  <Phone className="w-5 h-5 mr-2" />
                  04101 847 11 60
                </Button>
              </a>
              <a href={`https://wa.me/${COMPANY_INFO.whatsapp}`} target="_blank" rel="noopener noreferrer">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 text-lg px-10 py-6 rounded-xl font-bold">
                  <MessageCircle className="w-5 h-5 mr-2" />
                  WhatsApp
                </Button>
              </a>
            </div>
          </div>
        </section>

        <InternalLinksSection currentPage="/unfallgutachten-wiesbaden" />
      </main>

      <Footer />
    </div>
  );
};

export default UnfallgutachtenWiesbadenPage;
